'use client'

import React, { useCallback, useState } from 'react'
import { Box } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'

import type { HolidayPackage, HolidayPrice } from '@/src/types/holidays'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import useHolidayContacted from '@/src/hooks/holidays/useHolidayContacted'
import { HOLIDAY_ACTION, HOLIDAY_ACTION_TYPE } from '@/src/utils/analytics/holidays/holidayEvents'
import { formatHolidayPrice } from '@/src/utils/holidays'

import EnquiryCardWrapper from '../enquiryCard'

import MobileEnquiryBar from './mobileEnquiryBar'

/** Anchor the bar scrolls to; the enquiry card carries the same id. */
export const ENQUIRY_ANCHOR_ID = 'enquiry'

/** The sheet's own copy of the form, kept apart from the inline one's ids. */
const SHEET_FORM_ID = 'holiday-enquiry-sheet'

interface MobileEnquiryBarWrapperProps {
    startingPrice?: HolidayPrice
    priceLabel?: string
    enquiry?: HolidayPackage['enquiry']
}

const MobileEnquiryBarWrapper: React.FC<MobileEnquiryBarWrapperProps> = ({
    startingPrice,
    priceLabel,
    enquiry
}) => {
    const t = useTranslations('holidaysPage')
    const locale = useLocale()
    const { isContacted } = useHolidayContacted()
    const { triggerHolidayUIActionEvent } = useClevertapForHolidays()
    const [isSheetOpen, setIsSheetOpen] = useState(false)

    // The form comes to the reader rather than sending them up the page to find it.
    const handleCtaClick = useCallback(() => {
        setIsSheetOpen(true)
        triggerHolidayUIActionEvent(HOLIDAY_ACTION.ENQUIRY_OPENED, {
            action_type: HOLIDAY_ACTION_TYPE.FORM,
            form_name: SHEET_FORM_ID
        })
    }, [triggerHolidayUIActionEvent])

    const handleSheetClose = useCallback(() => setIsSheetOpen(false), [])

    if (!startingPrice || isContacted) return null

    return (
        <>
            <MobileEnquiryBar
                priceLabel={priceLabel ?? t('adultFrom')}
                price={formatHolidayPrice(startingPrice, locale)}
                ctaLabel={t('enquireNow')}
                onCtaClick={handleCtaClick}
            />

            {/*
             * Mounted from here whatever its state, which is what the shared drawer needs for its
             * back-button handling; only its contents are conditional.
             */}
            <SwipeableDrawerWithBackNavigation open={isSheetOpen} onClose={handleSheetClose} disableSwipeToOpen>
                {isSheetOpen && (
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pb: 3 }}>
                        <EnquiryCardWrapper
                            enquiry={enquiry}
                            startingPrice={startingPrice}
                            instanceId={SHEET_FORM_ID}
                            isMobile
                        />
                    </Box>
                )}
            </SwipeableDrawerWithBackNavigation>
        </>
    )
}

export { MobileEnquiryBarWrapper }
export default MobileEnquiryBarWrapper
